import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, ChevronLeft, ChevronRight, Eye } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { inboxTasksService } from '@/services/inboxTasksService'
import type {
  TaskNameItem,
  ApprovalStatusItem,
  InboxTaskListItem,
} from '@/api/types'

/**
 * Task Inbox list – Workflow Detail
 * Two dropdowns: Task name (InboxTasks/TaskNames), Statuses (InboxTasks/ApprovalStatuses).
 * All → taskId/statusId 0. List from InboxTasks/GetAll with paging.
 * Columns: Approval Task (name), Record Name, Approval Status (name), Created Date, Action (button), SeqNo.
 * No "Is Department Approve" or "Department" column.
 */
export const ApprovalJobs: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [taskNames, setTaskNames] = useState<TaskNameItem[]>([])
  const [statuses, setStatuses] = useState<ApprovalStatusItem[]>([])
  const [taskId, setTaskId] = useState<number>(0)
  const [statusId, setStatusId] = useState<number>(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [rows, setRows] = useState<InboxTaskListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  const fetchLookups = async () => {
    try {
      const [tasks, statusList] = await Promise.all([
        inboxTasksService.getTaskNames(),
        inboxTasksService.getApprovalStatuses(),
      ])
      setTaskNames(tasks ?? [])
      setStatuses(statusList ?? [])
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('taskInboxPage.toastFiltersFailed'))
    }
  }

  useEffect(() => {
    fetchLookups()
  }, [])

  const fetchList = async () => {
    if (!branchId) return
    try {
      setLoading(true)
      const res = await inboxTasksService.getAll(taskId, statusId, branchId, pageNumber, pageSize)
      setRows(res.data ?? [])
      setTotalRecords(res.totalRecords ?? 0)
      setTotalPages(
        res.totalRecords && res.pageSize
          ? Math.ceil(res.totalRecords / res.pageSize)
          : 0
      )
    } catch (e: any) {
      const msg = (e?.response?.data?.message || '').toLowerCase()
      const isNoData = /not found|no data|no records/.test(msg)
      setRows([])
      setTotalRecords(0)
      setTotalPages(0)
      if (!isNoData) toast.error(e?.response?.data?.message || t('taskInboxPage.toastListFailed'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchList()
  }, [branchId, taskId, statusId, pageNumber, pageSize])

  const taskNameById = useMemo(() => {
    const map: Record<number, string> = {}
    taskNames.forEach((task) => {
      if (task.id) map[task.id] = task.lookupNameEn || task.lookupNameAr || ''
    })
    return map
  }, [taskNames])

  const statusNameById = useMemo(() => {
    const map: Record<number, string> = {}
    statuses.forEach((s) => {
      if (s.id) map[s.id] = s.lookupNameEn || s.lookupNameAr || ''
    })
    return map
  }, [statuses])

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase()
    return rows.filter(
      (r) =>
        (recordName(r) || '').toLowerCase().includes(q) ||
        (taskNameById[r.approvalTaskId] || '').toLowerCase().includes(q) ||
        (statusNameById[r.approvalStatusId] || '').toLowerCase().includes(q)
    )
  }, [rows, searchQuery, taskNameById, statusNameById])

  function recordName(r: InboxTaskListItem) {
    return r.recordName ?? ''
  }

  function formatDate(dateStr?: string | null) {
    if (!dateStr) return '—'
    try {
      const d = new Date(dateStr)
      return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
    } catch {
      return dateStr
    }
  }

  const pagFrom = totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const pagTo = totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)
  const pageTotal = Math.max(1, totalPages || 1)

  const handlePreviousPage = () => {
    if (pageNumber > 1) setPageNumber((p) => p - 1)
  }

  const handleNextPage = () => {
    if (pageNumber < totalPages) setPageNumber((p) => p + 1)
  }

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const statusBadgeVariant = (statusText: string) => {
    const s = statusText.trim()
    const isApproved = /approved|معتمد/i.test(s)
    const isPending = /pending|قيد|انتظار/i.test(s)
    if (isApproved) return 'default' as const
    if (isPending) return 'secondary' as const
    return 'outline' as const
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('navigation.workflow')} description={t('taskInboxPage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('taskInboxPage.branchRequiredTitle')}
              description={t('taskInboxPage.branchRequiredDescription')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader title={t('navigation.workflow')} description={t('taskInboxPage.pageDescription')} />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('taskInboxPage.labelTaskName')}</label>
                <Select
                  value={taskId === 0 ? '0' : String(taskId)}
                  onValueChange={(v) => {
                    setTaskId(Number(v))
                    setPageNumber(1)
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder={t('taskInboxPage.placeholderAll')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{t('taskInboxPage.placeholderAll')}</SelectItem>
                    {taskNames
                      .filter((task) => task.id)
                      .map((task) => (
                        <SelectItem key={task.id} value={String(task.id)}>
                          {task.lookupNameEn || task.lookupNameAr || t('taskInboxPage.unnamed')}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">{t('taskInboxPage.labelStatuses')}</label>
                <Select
                  value={statusId === 0 ? '0' : String(statusId)}
                  onValueChange={(v) => {
                    setStatusId(Number(v))
                    setPageNumber(1)
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder={t('taskInboxPage.placeholderAll')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">{t('taskInboxPage.placeholderAll')}</SelectItem>
                    {statuses
                      .filter((s) => s.id)
                      .map((s) => (
                        <SelectItem key={s.id} value={String(s.id)}>
                          {s.lookupNameEn || s.lookupNameAr || t('taskInboxPage.unnamed')}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('taskInboxPage.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={t('taskInboxPage.emptyNoTasks')}
                description={searchQuery ? t('taskInboxPage.emptySearchHint') : t('taskInboxPage.emptyNoFilters')}
              />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                      {t('taskInboxPage.colApprovalTask')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                      {t('taskInboxPage.colRecordName')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                      {t('taskInboxPage.colApprovalStatus')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">
                      {t('taskInboxPage.colCreatedDate')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary w-24">
                      {t('taskInboxPage.colAction')}
                    </TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary w-20">
                      {t('taskInboxPage.colSeqNo')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.map((row) => (
                    <TableRow key={row.id} className="border-b hover:bg-primary/5">
                      <TableCell className="px-4 py-2 text-center align-middle">
                        {taskNameById[row.approvalTaskId] ?? '—'}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center align-middle">
                        {recordName(row) || '—'}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center align-middle">
                        {(() => {
                          const statusText = statusNameById[row.approvalStatusId] ?? '—'
                          return (
                            <Badge
                              variant={statusBadgeVariant(statusText)}
                              className="text-xs"
                            >
                              {statusText}
                            </Badge>
                          )
                        })()}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center align-middle">
                        {formatDate(row.createdDate)}
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center align-middle">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:bg-destructive/10"
                          asChild
                        >
                          <Link to={`/approval-jobs/${row.id}`}>
                            <Eye className="h-4 w-4" />
                          </Link>
                        </Button>
                      </TableCell>
                      <TableCell className="px-4 py-2 text-center align-middle">
                        {row.seqNo}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">{t('taskInboxPage.pageSize')}</span>
                  <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                    <SelectTrigger className="w-20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="text-sm text-muted-foreground">
                  {t('taskInboxPage.paginationShowing', { from: pagFrom, to: pagTo, total: totalRecords })}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePreviousPage}
                    disabled={pageNumber <= 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    {t('taskInboxPage.previous')}
                  </Button>
                  <span className="text-sm">
                    {t('taskInboxPage.pageOf', { page: pageNumber, totalPages: pageTotal })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleNextPage}
                    disabled={pageNumber >= totalPages || totalPages === 0}
                  >
                    {t('taskInboxPage.next')}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
